/**
 * AdminPlaysHub — control panel for the public plays hub.
 *
 * Layout
 * ──────
 * 1. KPI strip (totals, today, likes, comments, traffic, CTR, conversions, trending)
 * 2. Scheduler control (status, frequency, next run, queue size, pause/resume/publish-now)
 * 3. SEO tab (daily series chart, countries, referrers)
 * 4. Interaction tab (top likes / comments / shares)
 * 5. Library tab (table with filters + hide/unhide/delete + add forms)
 * 6. AI recommendations block (cached 1h on backend)
 *
 * All requests use the existing `api` axios helper (cookie/jwt auth handled
 * upstream). Admin-only route — the App.js wrapper enforces role.
 */
import React from 'react';
import Layout from '@/components/Layout';
import BackToDashboard from '@/components/BackToDashboard';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import {
  Activity, BarChart3, Heart, MessageSquare, Eye, Users,
  Play as PlayIcon, Pause, FastForward, Settings2, Sparkles,
  Plus, Trash2, EyeOff, Edit3, Layers, Wand2, RefreshCw,
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar,
} from 'recharts';

const FREQS = ['1/day', '2/day', '3/day', '1/2days', '1/week'];
const CATS = ['set', 'man', 'zone', 'ato', 'blob', 'slob', 'endgame', 'press_break', 'transition'];
const LANGS = ['es', 'en', 'fr'];
const TABS = [
  { id: 'overview', label: 'Resumen', icon: Activity },
  { id: 'seo', label: 'SEO local', icon: BarChart3 },
  { id: 'interaction', label: 'Interacción', icon: Heart },
  { id: 'library', label: 'Biblioteca', icon: Layers },
];

const fmt = (n) => Number(n || 0).toLocaleString('es-ES');

function KpiCard({ label, value, accent = 'text-gold' }) {
  return (
    <div className="rounded-xl border border-gold/20 bg-navy-deep p-4">
      <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-slate-400">{label}</div>
      <div className={`mt-2 font-h text-3xl tabular-nums ${accent}`}>{value}</div>
    </div>
  );
}

function SectionTitle({ icon: Icon, children, extra }) {
  return (
    <div className="flex items-end justify-between mb-3">
      <h2 className="font-h text-xl uppercase tracking-tight flex items-center gap-2">
        {Icon && <Icon className="w-5 h-5 text-gold" />} {children}
      </h2>
      {extra}
    </div>
  );
}

export default function AdminPlaysHub() {
  const [tab, setTab] = React.useState('overview');
  const [overview, setOverview] = React.useState(null);
  const [config, setConfig] = React.useState(null);
  const [seo, setSeo] = React.useState(null);
  const [interaction, setInteraction] = React.useState(null);
  const [library, setLibrary] = React.useState({ plays: [], total: 0 });
  const [recs, setRecs] = React.useState([]);
  const [statusFilter, setStatusFilter] = React.useState('');
  const [showCreate, setShowCreate] = React.useState(false);
  // Auto-images modal state — replaced `window.prompt` which was being
  // cancelled silently by some browsers / users, looking like the button
  // "did nothing".
  const [imgModalOpen, setImgModalOpen] = React.useState(false);
  const [imgCount, setImgCount] = React.useState(10);
  const [imgScope, setImgScope] = React.useState('queued');
  const [imgRunning, setImgRunning] = React.useState(false);
  const [imgResult, setImgResult] = React.useState(null);

  // Initial fetch (overview, config, recs always)
  const fetchOverview = React.useCallback(() => {
    api.get('/playshub/admin/overview').then((r) => setOverview(r.data)).catch(() => {});
    api.get('/playshub/admin/config').then((r) => setConfig(r.data)).catch(() => {});
    api.get('/playshub/admin/ai-recommendations').then((r) => setRecs(r.data?.recommendations || [])).catch(() => {});
  }, []);

  React.useEffect(() => { fetchOverview(); }, [fetchOverview]);

  React.useEffect(() => {
    if (tab === 'seo') {
      api.get('/playshub/admin/seo', { params: { days: 30 } }).then((r) => setSeo(r.data)).catch(() => {});
    }
    if (tab === 'interaction') {
      api.get('/playshub/admin/interaction').then((r) => setInteraction(r.data)).catch(() => {});
    }
    if (tab === 'library') {
      api.get('/playshub/admin/library', { params: { status: statusFilter || undefined, limit: 100 } })
        .then((r) => setLibrary(r.data)).catch(() => {});
    }
  }, [tab, statusFilter]);

  const onPause = async () => {
    await api.post('/playshub/admin/pause');
    toast.success('Publicaciones pausadas.');
    fetchOverview();
  };
  const onResume = async () => {
    await api.post('/playshub/admin/resume');
    toast.success('Publicaciones reactivadas.');
    fetchOverview();
  };
  const onFrequency = async (freq) => {
    await api.patch('/playshub/admin/frequency', { frequency: freq });
    toast.success(`Frecuencia: ${freq}`);
    fetchOverview();
  };
  const onPublishNow = async () => {
    try {
      const r = await api.post('/playshub/admin/publish-now');
      toast.success(`Publicada: ${r.data?.play?.title}`);
      fetchOverview();
      if (tab === 'library') {
        api.get('/playshub/admin/library', { params: { limit: 100 } }).then((r) => setLibrary(r.data));
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No hay jugadas en la cola.');
    }
  };
  const onBatchGen = async () => {
    const n = parseInt(window.prompt('¿Cuántas jugadas generar con IA? (1-20)', '10'), 10);
    if (!n || n < 1 || n > 20) return;
    const lang = window.prompt('Idioma (es / en / fr)', 'es') || 'es';
    toast.info(`Generando ${n} jugadas con Claude… (~${Math.ceil(n * 5 / 4)}s)`);
    try {
      const r = await api.post(`/playshub/admin/ai-generate-batch?count=${n}&language=${lang}`);
      toast.success(`Generadas: ${r.data.successes}/${r.data.requested} (en cola).`);
      fetchOverview();
      if (tab === 'library') {
        api.get('/playshub/admin/library', { params: { limit: 100 } }).then((r) => setLibrary(r.data));
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error en la generación batch.');
    }
  };
  const onFillImages = () => {
    // Opens an inline modal instead of `window.prompt` — the prompt was
    // being silently dismissed by some browser configurations / chrome
    // extensions, looking like the button did nothing. The modal gives the
    // admin a clear loading state and a persistent success/failure message.
    setImgResult(null);
    setImgModalOpen(true);
  };
  const runFillImages = async () => {
    const n = Number(imgCount);
    if (!n || n < 1 || n > 20) {
      toast.error('Indica un número entre 1 y 20.');
      return;
    }
    setImgRunning(true);
    setImgResult(null);
    toast.info(`Generando ${n} imágenes con Nano Banana… (~${Math.ceil(n * 6 / 4)}s)`);
    try {
      const r = await api.post(`/playshub/admin/ai-fill-images?limit=${n}&status=${imgScope}`);
      const summary = `Imágenes generadas: ${r.data.filled}/${r.data.candidates}` +
        (r.data.failed ? ` · ${r.data.failed} fallidas` : '');
      setImgResult({ ok: r.data.filled > 0, summary, details: r.data });
      toast.success(summary);
      fetchOverview();
      if (tab === 'library') {
        api.get('/playshub/admin/library', { params: { limit: 100 } }).then((r) => setLibrary(r.data));
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Error generando imágenes.';
      setImgResult({ ok: false, summary: detail });
      toast.error(detail);
    } finally {
      setImgRunning(false);
    }
  };
  const onRecompute = async () => {
    const r = await api.post('/playshub/admin/recompute');
    toast.success(`Recontado: ${r.data.total_published} publicadas, ${r.data.queue_size} en cola.`);
    fetchOverview();
  };

  return (
    <Layout>
      <BackToDashboard />
      <header className="mb-6">
        <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-gold mb-1">Admin</div>
        <h1 className="font-h text-4xl uppercase">Plays Hub · SEO + Scheduler</h1>
        <p className="text-sm text-slate-400 mt-2">
          Panel de control del marketplace público de jugadas. Métricas locales (no Google Search Console).
        </p>
      </header>

      {/* KPI strip */}
      {overview && (
        <section className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8" data-testid="ph-kpis">
          <KpiCard label="Jugadas totales" value={fmt(overview.total_published)} />
          <KpiCard label="Jugadas hoy" value={fmt(overview.today_count)} />
          <KpiCard label="Likes totales" value={fmt(overview.likes_total)} accent="text-rose-400" />
          <KpiCard label="Comentarios" value={fmt(overview.comments_total)} accent="text-sky-400" />
          <KpiCard label="Tráfico orgánico" value={fmt(overview.traffic_total)} accent="text-emerald-400" />
          <KpiCard label="CTR promedio" value={`${overview.ctr_avg}%`} accent="text-amber-400" />
          <KpiCard label="Suscriptores SEO" value={fmt(overview.subscribers_from_seo)} accent="text-purple-400" />
          <KpiCard label="En tendencia" value={fmt(overview.trending_count)} />
        </section>
      )}

      {/* Scheduler control */}
      {config && (
        <section className="mb-8 rounded-xl border border-gold/30 bg-navy-deep p-5" data-testid="ph-scheduler">
          <SectionTitle icon={Settings2}>Control de publicaciones automáticas</SectionTitle>
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400">Estado</div>
              <div className={`mt-1 inline-flex items-center gap-2 px-3 py-1 rounded-md text-xs font-mono uppercase tracking-widest ${
                config.enabled ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-400/40' : 'bg-rose-500/15 text-rose-300 border border-rose-400/40'
              }`} data-testid="ph-scheduler-status">
                <span className={`w-1.5 h-1.5 rounded-full ${config.enabled ? 'bg-emerald-400' : 'bg-rose-400'}`} />
                {config.enabled ? 'Activas' : 'Pausadas'}
              </div>
            </div>
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400">Frecuencia</div>
              <select
                data-testid="ph-frequency"
                value={config.frequency}
                onChange={(e) => onFrequency(e.target.value)}
                className="mt-1 bg-navy border border-gold/30 rounded-md px-2 py-1.5 text-xs font-mono uppercase tracking-widest"
              >
                {FREQS.map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400">Próxima publicación</div>
              <div className="mt-1 text-xs font-mono text-slate-200">
                {config.next_run_at ? new Date(config.next_run_at).toLocaleString('es-ES') : '—'}
              </div>
            </div>
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400">Jugadas en cola</div>
              <div className="mt-1 font-h text-2xl text-gold" data-testid="ph-queue-size">{fmt(config.queue_size)}</div>
            </div>
            <div className="ml-auto flex items-center gap-2 flex-wrap">
              {config.enabled ? (
                <button data-testid="ph-pause" onClick={onPause} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-rose-400/40 text-rose-300 text-xs font-mono uppercase tracking-widest hover:bg-rose-500/10">
                  <Pause className="w-3.5 h-3.5" /> Pausar
                </button>
              ) : (
                <button data-testid="ph-resume" onClick={onResume} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-emerald-400/40 text-emerald-300 text-xs font-mono uppercase tracking-widest hover:bg-emerald-500/10">
                  <PlayIcon className="w-3.5 h-3.5" /> Continuar
                </button>
              )}
              <button data-testid="ph-batch-gen" onClick={onBatchGen} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-purple-500 text-white text-xs font-mono uppercase tracking-widest hover:bg-purple-600">
                <Wand2 className="w-3.5 h-3.5" /> Generar IA batch
              </button>
              <button data-testid="ph-fill-images" onClick={onFillImages} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-pink-500 text-white text-xs font-mono uppercase tracking-widest hover:bg-pink-600">
                <Sparkles className="w-3.5 h-3.5" /> Auto imágenes
              </button>
              <button data-testid="ph-publish-now" onClick={onPublishNow} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-gold text-navy-deep text-xs font-mono uppercase tracking-widest hover:bg-gold/90">
                <FastForward className="w-3.5 h-3.5" /> Publicar ahora
              </button>
              <button data-testid="ph-recompute" onClick={onRecompute} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-gold/30 text-slate-200 text-xs font-mono uppercase tracking-widest hover:bg-gold/10">
                <RefreshCw className="w-3.5 h-3.5" /> Recalcular
              </button>
            </div>
          </div>
        </section>
      )}

      {/* AI Recommendations */}
      {recs.length > 0 && (
        <section className="mb-8 rounded-xl border border-purple-400/30 bg-purple-500/5 p-5" data-testid="ph-ai-recs">
          <SectionTitle icon={Sparkles}>Recomendaciones IA</SectionTitle>
          <ul className="space-y-2">
            {recs.map((r, i) => (
              <li key={i} className="text-sm text-slate-200 flex items-start gap-2">
                <span className="text-purple-300">📌</span>
                <span>{r}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Tabs */}
      <div className="mb-6 flex items-center gap-2 border-b border-white/10">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            data-testid={`ph-tab-${id}`}
            onClick={() => setTab(id)}
            className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs font-mono uppercase tracking-widest border-b-2 transition-colors ${
              tab === id ? 'border-gold text-gold' : 'border-transparent text-slate-300 hover:text-gold'
            }`}
          >
            <Icon className="w-3.5 h-3.5" /> {label}
          </button>
        ))}
      </div>

      {tab === 'overview' && (
        <OverviewTab overview={overview} />
      )}
      {tab === 'seo' && (
        <SeoTab seo={seo} />
      )}
      {tab === 'interaction' && (
        <InteractionTab interaction={interaction} />
      )}
      {tab === 'library' && (
        <LibraryTab
          data={library}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
          onShowCreate={() => setShowCreate(true)}
          onChanged={() => {
            api.get('/playshub/admin/library', { params: { status: statusFilter || undefined, limit: 100 } })
              .then((r) => setLibrary(r.data));
            fetchOverview();
          }}
        />
      )}

      {showCreate && (
        <CreateModal
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            api.get('/playshub/admin/library', { params: { limit: 100 } }).then((r) => setLibrary(r.data));
            fetchOverview();
          }}
        />
      )}

      {imgModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          onClick={() => !imgRunning && setImgModalOpen(false)}
          data-testid="ph-fill-images-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-xl border border-pink-400/40 bg-navy-deep p-6 space-y-4"
          >
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-pink-400" />
              <h3 className="font-h text-lg uppercase tracking-tight">Auto-rellenar imágenes (Nano Banana)</h3>
            </div>
            <p className="text-xs text-slate-400">
              Genera imágenes tácticas para las jugadas que aún no tienen <code className="text-gold">image_partial_url</code>.
              Cada imagen tarda ~6s y cuesta tokens del Emergent LLM Key.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] font-mono uppercase tracking-widest text-slate-400">Cuántas (1–20)</label>
                <input
                  type="number"
                  min={1}
                  max={20}
                  value={imgCount}
                  onChange={(e) => setImgCount(e.target.value)}
                  disabled={imgRunning}
                  data-testid="ph-fill-images-count"
                  className="mt-1 w-full bg-navy border border-gold/30 rounded-md px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="text-[10px] font-mono uppercase tracking-widest text-slate-400">Alcance</label>
                <select
                  value={imgScope}
                  onChange={(e) => setImgScope(e.target.value)}
                  disabled={imgRunning}
                  data-testid="ph-fill-images-scope"
                  className="mt-1 w-full bg-navy border border-gold/30 rounded-md px-3 py-2 text-sm"
                >
                  <option value="queued">Solo en cola</option>
                  <option value="published">Solo publicadas</option>
                  <option value="any">Cualquier estado</option>
                </select>
              </div>
            </div>
            {imgResult && (
              <div
                className={`text-xs px-3 py-2 rounded-md border ${
                  imgResult.ok
                    ? 'border-emerald-400/40 bg-emerald-400/10 text-emerald-200'
                    : 'border-rose-400/40 bg-rose-400/10 text-rose-200'
                }`}
                data-testid="ph-fill-images-result"
              >
                {imgResult.summary}
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => setImgModalOpen(false)}
                disabled={imgRunning}
                className="px-3 py-2 rounded-md border border-gold/30 text-slate-300 text-xs font-mono uppercase tracking-widest hover:bg-gold/10 disabled:opacity-40"
                data-testid="ph-fill-images-cancel"
              >
                {imgRunning ? '...' : 'Cerrar'}
              </button>
              <button
                onClick={runFillImages}
                disabled={imgRunning}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-pink-500 text-white text-xs font-mono uppercase tracking-widest hover:bg-pink-600 disabled:opacity-40"
                data-testid="ph-fill-images-run"
              >
                {imgRunning ? (
                  <>
                    <RefreshCw className="w-3.5 h-3.5 animate-spin" /> Generando...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-3.5 h-3.5" /> Generar
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

// ── Tabs ──────────────────────────────────────────────────────────────

function OverviewTab({ overview }) {
  if (!overview) return null;
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div className="md:col-span-2 rounded-xl border border-gold/20 bg-navy-deep p-5">
        <div className="font-h text-lg uppercase mb-2">Vista rápida</div>
        <p className="text-sm text-slate-400">
          Tu marketplace público tiene <span className="text-gold font-semibold">{fmt(overview.total_published)}</span> jugadas publicadas con <span className="text-rose-300">{fmt(overview.likes_total)}</span> likes y <span className="text-sky-300">{fmt(overview.comments_total)}</span> comentarios. CTR promedio <span className="text-amber-300">{overview.ctr_avg}%</span> sobre <span className="text-emerald-300">{fmt(overview.traffic_total)}</span> visitas. <span className="text-purple-300">{fmt(overview.subscribers_from_seo)}</span> usuarios se convirtieron desde la página pública.
        </p>
      </div>
      <div className="rounded-xl border border-gold/20 bg-navy-deep p-5">
        <div className="font-h text-lg uppercase mb-2">Cola</div>
        <div className="font-h text-4xl text-gold">{fmt(overview.total_queued)}</div>
        <div className="text-xs text-slate-400 mt-1">jugadas listas para publicar</div>
        <div className="mt-3 text-[11px] font-mono uppercase tracking-widest text-slate-500">
          {overview.total_hidden} ocultas · {overview.total_published} públicas
        </div>
      </div>
    </div>
  );
}

function SeoTab({ seo }) {
  if (!seo) return <div className="text-sm text-slate-400">Cargando…</div>;
  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-gold/20 bg-navy-deep p-5">
        <SectionTitle icon={BarChart3}>Tráfico local (30 días)</SectionTitle>
        <p className="text-[11px] text-slate-400 mb-3 italic">{seo.note_local_only}</p>
        <div className="h-64">
          <ResponsiveContainer>
            <LineChart data={seo.series}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" />
              <XAxis dataKey="day" stroke="#94A3B8" fontSize={10} />
              <YAxis stroke="#94A3B8" fontSize={10} />
              <Tooltip contentStyle={{ background: '#0A192F', border: '1px solid #D4AF37' }} />
              <Line type="monotone" dataKey="views" stroke="#10B981" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="cta_clicks" stroke="#D4AF37" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="conversions" stroke="#A855F7" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-xl border border-gold/20 bg-navy-deep p-5">
          <SectionTitle icon={Users}>Países (top 10)</SectionTitle>
          {(seo.countries || []).length === 0 ? (
            <div className="text-sm text-slate-500 italic">Sin datos aún.</div>
          ) : (
            <div className="h-56">
              <ResponsiveContainer>
                <BarChart data={seo.countries}>
                  <XAxis dataKey="country" stroke="#94A3B8" fontSize={10} />
                  <YAxis stroke="#94A3B8" fontSize={10} />
                  <Tooltip contentStyle={{ background: '#0A192F', border: '1px solid #D4AF37' }} />
                  <Bar dataKey="views" fill="#D4AF37" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
        <div className="rounded-xl border border-gold/20 bg-navy-deep p-5">
          <SectionTitle icon={Activity}>Referrers (top 10)</SectionTitle>
          {(seo.referrers || []).length === 0 ? (
            <div className="text-sm text-slate-500 italic">Sin referrers todavía.</div>
          ) : (
            <ul className="space-y-1.5 text-xs">
              {seo.referrers.map((r, i) => (
                <li key={i} className="flex items-center justify-between gap-2">
                  <span className="truncate text-slate-200">{r.referrer}</span>
                  <span className="text-gold font-mono">{r.n}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function InteractionTab({ interaction }) {
  if (!interaction) return <div className="text-sm text-slate-400">Cargando…</div>;
  const lists = [
    { title: 'Top likes', items: interaction.top_likes, metric: 'likes_count', icon: Heart, color: 'text-rose-400' },
    { title: 'Top comentarios', items: interaction.top_comments, metric: 'comments_count', icon: MessageSquare, color: 'text-sky-400' },
    { title: 'Top shares', items: interaction.top_shares, metric: 'shares_count', icon: Eye, color: 'text-emerald-400' },
  ];
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {lists.map(({ title, items, metric, icon: Icon, color }) => (
        <div key={title} className="rounded-xl border border-gold/20 bg-navy-deep p-5">
          <h3 className="font-h text-lg uppercase mb-3 flex items-center gap-2">
            <Icon className={`w-4 h-4 ${color}`} /> {title}
          </h3>
          {(items || []).length === 0 ? (
            <div className="text-xs text-slate-500 italic">Sin datos.</div>
          ) : (
            <ol className="space-y-2 text-sm">
              {items.map((p, i) => (
                <li key={p.id} className="flex items-center justify-between gap-2">
                  <span className="truncate text-slate-200">{i + 1}. {p.title}</span>
                  <span className={`font-mono ${color}`}>{fmt(p[metric])}</span>
                </li>
              ))}
            </ol>
          )}
        </div>
      ))}
    </div>
  );
}

function LibraryTab({ data, statusFilter, setStatusFilter, onShowCreate, onChanged }) {
  const onSetStatus = async (id, status) => {
    await api.patch(`/playshub/admin/plays/${id}/status?status=${status}`);
    toast.success(`Jugada → ${status}`);
    onChanged();
  };
  const onDelete = async (id) => {
    if (!window.confirm('¿Eliminar definitivamente esta jugada?')) return;
    await api.delete(`/playshub/admin/plays/${id}`);
    toast.success('Jugada eliminada.');
    onChanged();
  };
  return (
    <div className="rounded-xl border border-gold/20 bg-navy-deep p-5">
      <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-mono uppercase tracking-widest text-slate-400">Estado:</span>
          {['', 'queued', 'published', 'hidden'].map((s) => (
            <button
              key={s || 'all'}
              data-testid={`ph-filter-${s || 'all'}`}
              onClick={() => setStatusFilter(s)}
              className={`px-2.5 py-1 rounded-md text-[10px] font-mono uppercase tracking-widest border ${
                statusFilter === s ? 'border-gold text-gold' : 'border-white/15 text-slate-300 hover:border-gold/50'
              }`}
            >
              {s || 'todos'}
            </button>
          ))}
        </div>
        <button data-testid="ph-create-open" onClick={onShowCreate} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-gold text-navy-deep text-xs font-mono uppercase tracking-widest">
          <Plus className="w-3.5 h-3.5" /> Crear jugada
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] font-mono uppercase tracking-widest text-slate-400 border-b border-white/10">
              <th className="py-2 pr-3">Jugada</th>
              <th className="py-2 pr-3">Estado</th>
              <th className="py-2 pr-3 text-right">Likes</th>
              <th className="py-2 pr-3 text-right">Comentarios</th>
              <th className="py-2 pr-3 text-right">Views</th>
              <th className="py-2 pr-3 text-right">SEO</th>
              <th className="py-2 pr-3">Origen</th>
              <th className="py-2 pr-3">Acciones</th>
            </tr>
          </thead>
          <tbody data-testid="ph-library-rows">
            {(data.plays || []).map((p) => (
              <tr key={p.id} className="border-b border-white/5 hover:bg-white/5">
                <td className="py-2 pr-3">
                  <div className="text-slate-100 font-medium truncate max-w-[260px]">{p.title}</div>
                  <div className="text-[10px] text-slate-500 font-mono">{p.slug}</div>
                </td>
                <td className="py-2 pr-3">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-mono uppercase tracking-widest border ${
                    p.status === 'published' ? 'border-emerald-400/40 text-emerald-300' :
                    p.status === 'queued' ? 'border-amber-400/40 text-amber-300' :
                    'border-rose-400/40 text-rose-300'
                  }`}>{p.status}</span>
                </td>
                <td className="py-2 pr-3 text-right text-rose-300 font-mono">{fmt(p.likes_count)}</td>
                <td className="py-2 pr-3 text-right text-sky-300 font-mono">{fmt(p.comments_count)}</td>
                <td className="py-2 pr-3 text-right text-emerald-300 font-mono">{fmt(p.views_count)}</td>
                <td className="py-2 pr-3 text-right text-gold font-mono">{p.seo_score}/100</td>
                <td className="py-2 pr-3 text-[10px] font-mono uppercase tracking-widest text-slate-400">{p.source}</td>
                <td className="py-2 pr-3">
                  <div className="flex items-center gap-1">
                    {p.status !== 'published' && (
                      <button data-testid={`ph-publish-${p.id}`} onClick={() => onSetStatus(p.id, 'published')} className="p-1.5 rounded border border-emerald-400/30 text-emerald-300 hover:bg-emerald-500/10" title="Publicar">
                        <PlayIcon className="w-3 h-3" />
                      </button>
                    )}
                    {p.status !== 'hidden' && (
                      <button data-testid={`ph-hide-${p.id}`} onClick={() => onSetStatus(p.id, 'hidden')} className="p-1.5 rounded border border-rose-400/30 text-rose-300 hover:bg-rose-500/10" title="Ocultar">
                        <EyeOff className="w-3 h-3" />
                      </button>
                    )}
                    <button data-testid={`ph-delete-${p.id}`} onClick={() => onDelete(p.id)} className="p-1.5 rounded border border-white/15 text-slate-400 hover:bg-white/5" title="Eliminar">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Create modal — supports all 3 sources (manual / playbook / AI) ─────

function CreateModal({ onClose, onCreated }) {
  const [mode, setMode] = React.useState('manual'); // manual | playbook | ai
  const [busy, setBusy] = React.useState(false);
  const [form, setForm] = React.useState({
    title: '',
    summary_partial: '',
    summary_full: '',
    category: 'set',
    tags: '',
    language: 'es',
    image_url: '',
  });
  const [playbookId, setPlaybookId] = React.useState('');
  const [aiCat, setAiCat] = React.useState('set');
  const [aiLang, setAiLang] = React.useState('es');

  const submitManual = async () => {
    if (form.title.length < 3 || form.summary_partial.length < 10) {
      toast.error('Título ≥ 3 chars y resumen ≥ 10 chars.');
      return;
    }
    setBusy(true);
    try {
      await api.post('/playshub/admin/plays', {
        ...form,
        tags: form.tags.split(',').map((t) => t.trim()).filter(Boolean),
        source: 'manual',
        image_partial_url: form.image_url,
      });
      toast.success('Jugada creada (en cola).');
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error.');
    } finally { setBusy(false); }
  };

  const submitPlaybook = async () => {
    if (!playbookId.trim()) { toast.error('ID requerido'); return; }
    setBusy(true);
    try {
      await api.post(`/playshub/admin/from-playbook/${playbookId.trim()}`);
      toast.success('Jugada importada del playbook (en cola).');
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error.');
    } finally { setBusy(false); }
  };

  const submitAi = async () => {
    setBusy(true);
    try {
      await api.post(`/playshub/admin/ai-generate?category=${aiCat}&language=${aiLang}`);
      toast.success('Jugada generada con IA (en cola).');
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error.');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-center justify-center p-4" data-testid="ph-create-modal">
      <div className="w-full max-w-2xl rounded-xl border border-gold/30 bg-navy-deep p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-h text-xl uppercase">Crear jugada</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white text-xs font-mono uppercase tracking-widest">cerrar</button>
        </div>
        <div className="flex items-center gap-1 mb-4">
          {[
            { id: 'manual', icon: Edit3, label: 'Manual' },
            { id: 'playbook', icon: Layers, label: 'Desde Playbook' },
            { id: 'ai', icon: Wand2, label: 'Generar con IA' },
          ].map(({ id, icon: Icon, label }) => (
            <button
              key={id}
              data-testid={`ph-mode-${id}`}
              onClick={() => setMode(id)}
              className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-mono uppercase tracking-widest border ${
                mode === id ? 'border-gold text-gold' : 'border-white/15 text-slate-300 hover:border-gold/50'
              }`}
            >
              <Icon className="w-3.5 h-3.5" /> {label}
            </button>
          ))}
        </div>

        {mode === 'manual' && (
          <div className="space-y-3">
            <input data-testid="ph-input-title" placeholder="Título" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full bg-navy border border-gold/20 rounded-md px-3 py-2 text-sm" />
            <textarea data-testid="ph-input-summary" placeholder="Resumen parcial (público, 90-220 chars ideal)" value={form.summary_partial} onChange={(e) => setForm({ ...form, summary_partial: e.target.value })} rows={3} className="w-full bg-navy border border-gold/20 rounded-md px-3 py-2 text-sm" />
            <textarea placeholder="Resumen completo (solo logueados)" value={form.summary_full} onChange={(e) => setForm({ ...form, summary_full: e.target.value })} rows={4} className="w-full bg-navy border border-gold/20 rounded-md px-3 py-2 text-sm" />
            <div className="grid grid-cols-3 gap-2">
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="bg-navy border border-gold/20 rounded-md px-2 py-2 text-xs">
                {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <select value={form.language} onChange={(e) => setForm({ ...form, language: e.target.value })} className="bg-navy border border-gold/20 rounded-md px-2 py-2 text-xs">
                {LANGS.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
              <input placeholder="tags,separadas,por,coma" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} className="bg-navy border border-gold/20 rounded-md px-2 py-2 text-xs" />
            </div>
            <input placeholder="URL imagen (opcional, snapshot del play editor)" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className="w-full bg-navy border border-gold/20 rounded-md px-3 py-2 text-sm" />
            <button data-testid="ph-submit-manual" onClick={submitManual} disabled={busy} className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-gold text-navy-deep font-mono uppercase tracking-widest text-xs disabled:opacity-50">
              {busy ? 'Creando…' : 'Crear y mandar a la cola'}
            </button>
          </div>
        )}

        {mode === 'playbook' && (
          <div className="space-y-3">
            <p className="text-xs text-slate-400">Pegá el ID de una jugada existente del Playbook privado. Vamos a copiarla a la cola pública.</p>
            <input data-testid="ph-input-playbook-id" placeholder="ID de la jugada" value={playbookId} onChange={(e) => setPlaybookId(e.target.value)} className="w-full bg-navy border border-gold/20 rounded-md px-3 py-2 text-sm font-mono" />
            <button data-testid="ph-submit-playbook" onClick={submitPlaybook} disabled={busy} className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-gold text-navy-deep font-mono uppercase tracking-widest text-xs disabled:opacity-50">
              {busy ? 'Importando…' : 'Importar al hub'}
            </button>
          </div>
        )}

        {mode === 'ai' && (
          <div className="space-y-3">
            <p className="text-xs text-slate-400">Claude va a inventar una jugada con título SEO, resumen parcial y completo. Vos podés editar la imagen después.</p>
            <div className="grid grid-cols-2 gap-2">
              <select data-testid="ph-ai-cat" value={aiCat} onChange={(e) => setAiCat(e.target.value)} className="bg-navy border border-gold/20 rounded-md px-2 py-2 text-xs">
                {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <select data-testid="ph-ai-lang" value={aiLang} onChange={(e) => setAiLang(e.target.value)} className="bg-navy border border-gold/20 rounded-md px-2 py-2 text-xs">
                {LANGS.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
            <button data-testid="ph-submit-ai" onClick={submitAi} disabled={busy} className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-purple-500 text-white font-mono uppercase tracking-widest text-xs disabled:opacity-50">
              <Wand2 className="w-3.5 h-3.5" /> {busy ? 'Generando…' : 'Generar con Claude'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
